/**
 * MOANI Price Service
 * Fetches MOANI price from CoinGecko API with fallback and caching
 */

const FALLBACK_PRICE = 0.0025; // $0.0025 per MOANI token
const CACHE_KEY = 'moani_price_cache';
const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

class MoaniPriceService {
  constructor() {
    this.cachedPrice = null;
    this.cacheTimestamp = null;
    this.fetchPromise = null;
  }

  /**
   * Get cached price from localStorage
   */
  getCachedPrice() {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { price, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid (5 minutes)
        if (now - timestamp < CACHE_DURATION) {
          this.cachedPrice = price;
          this.cacheTimestamp = timestamp;
          return price;
        }
      }
    } catch (error) {
      console.warn('Error reading MOANI price cache:', error);
    }
    return null;
  }

  /**
   * Cache price in localStorage
   */
  setCachedPrice(price) {
    try {
      const cacheData = {
        price,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      this.cachedPrice = price;
      this.cacheTimestamp = cacheData.timestamp;
    } catch (error) {
      console.warn('Error caching MOANI price:', error);
    }
  }

  /**
   * Fetch MOANI price from backend API
   */
  async fetchPriceFromAPI() {
    try {
      const env = localStorage.getItem('selectedEnvironment') || 'production';
      const apiPrefix = env === 'development' ? '/api-dev' : '/api';
      const apiUrl = window.location.origin;
      const response = await fetch(`${apiUrl}${apiPrefix}/moani-price`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include' // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.price) {
        console.log('✅ MOANI price fetched from backend:', {
          price: data.price,
          source: data.source,
          isFromAPI: data.isFromAPI,
          lastUpdated: data.lastUpdated
        });
        return data.price;
      }
      
      throw new Error('Invalid response from backend API');
    } catch (error) {
      console.warn('❌ Failed to fetch MOANI price from backend:', error);
      throw error;
    }
  }

  /**
   * Get current MOANI price with caching
   * Returns cached price immediately if available, otherwise fetches from API
   */
  async getPrice() {
    // Check cache first
    const cachedPrice = this.getCachedPrice();
    if (cachedPrice !== null) {
      return cachedPrice;
    }

    // If already fetching, return the same promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Fetch from API
    this.fetchPromise = this.fetchPriceFromAPI()
      .then(price => {
        this.setCachedPrice(price);
        this.fetchPromise = null;
        return price;
      })
      .catch(error => {
        console.warn('Using fallback MOANI price:', FALLBACK_PRICE);
        this.setCachedPrice(FALLBACK_PRICE);
        this.fetchPromise = null;
        return FALLBACK_PRICE;
      });

    return this.fetchPromise;
  }

  /**
   * Get price synchronously from cache, or return fallback
   */
  getPriceSync() {
    const cachedPrice = this.getCachedPrice();
    return cachedPrice !== null ? cachedPrice : FALLBACK_PRICE;
  }

  /**
   * Force refresh price from backend API
   */
  async refreshPrice() {
    try {
      // Clear local cache
      localStorage.removeItem(CACHE_KEY);
      this.cachedPrice = null;
      this.cacheTimestamp = null;
      this.fetchPromise = null;

      // Call backend refresh endpoint
      const env = localStorage.getItem('selectedEnvironment') || 'production';
      const apiPrefix = env === 'development' ? '/api-dev' : '/api';
      const apiUrl = window.location.origin;
      const response = await fetch(`${apiUrl}${apiPrefix}/moani-price/refresh`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.price) {
          // Cache the refreshed price
          this.setCachedPrice(data.price);
          console.log('✅ MOANI price force refreshed:', data.price);
          return data.price;
        }
      }
    } catch (error) {
      console.warn('⚠️ Force refresh failed, falling back to regular fetch:', error);
    }

    // Fallback to regular price fetch
    return this.getPrice();
  }

  /**
   * Calculate USD value for MOANI amount
   */
  calculateUSDValue(moaniAmount, price = null) {
    const currentPrice = price || this.getPriceSync();
    const amount = parseFloat(moaniAmount) || 0;
    return amount * currentPrice;
  }
}

// Export singleton instance
export const moaniPriceService = new MoaniPriceService();
export default moaniPriceService;
